import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { UserState } from "@/context/User/UserReducer";
import { getDesignations } from "@/services/Designation.service";
import { addReporter } from "@/services/Reporter.service";
import { AddReporterParams } from "@/types/reporter.types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "../ui/form";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";

const formSchema = z.object({
	reportername: z.string(),
	designationid: z.number(),
});

const CreateReporter: React.FC<{ user: UserState | null }> = ({ user }) => {
	const [open, setOpen] = useState(false);

	const queryClient = useQueryClient();

	const designations = useQuery({
		queryKey: ["designations"],
		queryFn: () => getDesignations(user?.accessToken!),
		enabled: !!user?.accessToken,
		staleTime: 3000,
	});

	const createReporter = useMutation({
		mutationKey: ["reporter_create"],
		mutationFn: (data: AddReporterParams) =>
			addReporter(user?.accessToken!, data),
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({
				queryKey: ["reporters"],
				exact: true,
				refetchType: "all",
			});
			setOpen(false);
			form.reset();
			toast.success("Success", {
				description: `Reporter ${variables.reportername} has been successfully created`,
			});
			createReporter.reset();
		},
		onError: (err) => {
			toast.error("Error!", { description: "Error adding reporter, try again" });
			console.log(err);
		},
	});

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
	});

	const onSubmit = (data: z.infer<typeof formSchema>) => {
		console.log(data);
		createReporter.mutate(data);
	};

	const disabled = () => {
		if (createReporter.isPending) return true;
		if (createReporter.isSuccess) return true;
		return false;
	};

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>
				<Button variant="default">Add Reporter</Button>
			</DialogTrigger>
			<DialogContent className="sm:max-w-xl">
				<DialogHeader>
					<DialogTitle className="text-left font-bold text-2xl">
						Add Reporter
					</DialogTitle>
				</DialogHeader>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)}>
						<FormField
							name="reportername"
							render={({ field }) => (
								<FormItem className="flex-1 mb-2">
									<FormLabel>Name of Reporter</FormLabel>
									<FormControl>
										<Input type="text" {...field} disabled={disabled()} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						{designations.isLoading ? (
							<>Loading Designations</>
						) : designations.data ? (
							<FormField
								name="designationid"
								render={({ field }) => (
									<FormItem className="mb-2 flex-1">
										<FormLabel>Case Type</FormLabel>
										<FormControl>
											<Select
												onValueChange={(e) => {
													field.onChange(parseFloat(e));
												}}
												disabled={disabled()}
											>
												<SelectTrigger>
													<SelectValue placeholder="Select an option" />
												</SelectTrigger>
												<SelectContent>
													{designations.data.map((item) => (
														<SelectItem value={item.designationid.toString()}>
															{item.designation}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						) : null}
						<Button
							type="submit"
							className="mt-4 w-full"
							disabled={createReporter.isPending || createReporter.isSuccess}
						>
							{createReporter.isPending ? "Creating..." : "Create Reporter"}
						</Button>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	);
};

export default CreateReporter;
